import type { Bar, Pivot, Timeframe } from '../types';
import { calculateRsi, classifyRegime, defaultConfig, findConfirmedPivots, type AnalysisConfig } from './analysis';

export interface ReplayEvent {
  id: string;
  timeframe: Timeframe;
  type: 'higher-low' | 'lower-high' | 'bullish-divergence' | 'bearish-divergence';
  pivotTime: number;
  detectedAt: number;
  price: number;
  regime: string;
  explanation: string;
}

// A historical study of observations, not hypothetical fills or strategy P&L.
// Every event is dated when the right-hand confirming bars existed, never at the pivot.
export function replayConfirmedConditions(bars: Bar[], timeframe: Timeframe, config: AnalysisConfig = defaultConfig): ReplayEvent[] {
  const pivots = findConfirmedPivots(bars, config);
  const rsi = calculateRsi(bars, config.rsiPeriod);
  const out: ReplayEvent[] = [];
  const previous: Partial<Record<Pivot['kind'], Pivot>> = {};
  for (const pivot of pivots) {
    const prior = previous[pivot.kind];
    const confirmedIndex = bars.findIndex(b => b.time === pivot.confirmedAt);
    if (confirmedIndex < 0) continue;
    const asKnown = bars.slice(0, confirmedIndex + 1);
    const regime = classifyRegime(asKnown, timeframe, config).regime;
    const add = (type: ReplayEvent['type'], explanation: string) => out.push({
      id: `${timeframe}:${type}:${pivot.confirmedAt}`, timeframe, type,
      pivotTime: pivot.time, detectedAt: pivot.confirmedAt, price: pivot.price, regime, explanation
    });
    if (prior && pivot.label === 'HL') add('higher-low', 'Confirmed low above the previous confirmed low.');
    if (prior && pivot.label === 'LH') add('lower-high', 'Confirmed high below the previous confirmed high.');
    if (prior && pivot.kind === 'low' && pivot.price < prior.price && rsi[pivot.index] != null &&
      rsi[prior.index] != null && rsi[pivot.index]! > rsi[prior.index]!)
      add('bullish-divergence', 'Price made a lower low while RSI at the confirmed pivots made a higher low.');
    if (prior && pivot.kind === 'high' && pivot.price > prior.price && rsi[pivot.index] != null &&
      rsi[prior.index] != null && rsi[pivot.index]! < rsi[prior.index]!)
      add('bearish-divergence', 'Price made a higher high while RSI at the confirmed pivots made a lower high.');
    previous[pivot.kind] = pivot;
  }
  return out.sort((a, b) => a.detectedAt - b.detectedAt);
}
